import type { GQL_ID } from '@@partner:types:graphql'
import type { OrderPosition } from '@@partner:types:app'

import React, { useContext, useEffect, useState, useMemo } from 'react'

export interface RefundStateContextValue {
  checkedIds: GQL_ID[]
  readyToRefundPositions: RefundPositionInfo[]
  checkedPositions: RefundPositionInfo[]
  refundPositionsInfo: RefundPositionInfo[]
  refundablePositions: RefundPositionInfo[]
  groupedPositions: Record<GQL_ID, RefundPositionInfo>
  isAllChecked: boolean
  canRefund: boolean
  makeRefund: boolean
  activateRefund: () => void
  updateRefundAmount(positionId: GQL_ID, refundAmount: number): void
  checkAll(check?: boolean): void
  toggleChecked(selectedIds: GQL_ID[]): void
}

export interface RefundPositionInfo {
  id: GQL_ID
  checked: boolean
  isRefundable: boolean
  refundAmount: number
  position: OrderPosition
}

export interface RefundStateProviderProps {
  shop_id?: GQL_ID
  initial?: OrderPosition[]
}

// Init context here
const RefundStateContext = React.createContext<RefundStateContextValue>(
  {} as RefundStateContextValue
)

const isPositionRefundable = (position: OrderPosition) => {
  return position.status === 5
}

const RefundStateProvider: React.FC<RefundStateProviderProps> = ({
  children,
  initial
}) => {
  const [ refundPositionsInfo, setRefundPositionsInfo ] = useState<
    RefundPositionInfo[]
  >([])
  const [ refundIsActive, setRefundIsActive ] = useState<boolean>(false)

  useEffect(() => {
    if (initial && initial.length > 0) {
      setRefundPositionsInfo(
        initial.map((position) => ({
          id: position.id,
          refundAmount: 0,
          checked: false,
          isRefundable: isPositionRefundable(position),
          position
        }))
      )
    }
  }, [ initial ])

  const value = useMemo<RefundStateContextValue>(
    () => ({
      get checkedPositions () {
        return refundPositionsInfo.filter(({ checked }) => !!checked)
      },

      get readyToRefundPositions () {
        return this.checkedPositions.filter(
          ({ refundAmount = 0 }) => refundAmount > 0
        )
      },

      get checkedIds () {
        return this.checkedPositions.map(({ id }) => id)
      },

      get refundablePositions () {
        return refundPositionsInfo.filter(({ isRefundable }) => isRefundable)
      },

      get isAllChecked () {
        return (
          this.checkedPositions.length > 0 &&
          this.checkedPositions.length === this.refundablePositions.length
        )
      },

      get groupedPositions () {
        return refundPositionsInfo.reduce(
          (prev, curr) => ({ ...prev, [curr.id]: curr }),
          {}
        )
      },

      get canRefund () {
        return this.readyToRefundPositions.length > 0
      },

      get makeRefund () {
        return refundIsActive
      },
      activateRefund () {
          setRefundIsActive(!refundIsActive)
      },

      refundPositionsInfo,

      updateRefundAmount (positionId: GQL_ID, refundAmount = 0) {
        setRefundPositionsInfo((old) => {
          const item = old.find(({ id }) => id === positionId)

          if (item && item.checked) {
            return old.map((item) =>
              item.id === positionId ? { ...item, refundAmount } : item
            )
          }

          return old
        })
      },

      checkAll (checked = true) {
        setRefundPositionsInfo((old) =>
          old.map((item) => (item.isRefundable ? { ...item, checked, refundAmount: item.position.assembled_count } : item))
        )
      },

      toggleChecked (selectedIds = []) {
        setRefundPositionsInfo((old) =>
          old.map((item) =>
            selectedIds.includes(item.id)
              ? { ...item, checked: true }
              : { ...item, checked: false }
          )
        )
      }
    }),
    [ refundPositionsInfo, refundIsActive, setRefundIsActive ]
  )

  return (
    <RefundStateContext.Provider value={value}>
      {children}
    </RefundStateContext.Provider>
  )
}

export const useRefundState = () => useContext(RefundStateContext)

const RefundStateContextConsumer = RefundStateContext.Consumer

export {
  RefundStateContext,
  RefundStateProvider as RefundStateContextProvider,
  RefundStateContextConsumer
}
